import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Select, Spin, Typography, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { authAPI } from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

function Field({ label, value }) {
  const v = value === null || value === undefined || value === '' ? '-' : String(value);
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
      <div style={{ width: 80, color: '#666', flex: '0 0 auto' }}>{label}</div>
      <div style={{ color: '#111', flex: 1, wordBreak: 'break-all' }}>{v}</div>
    </div>
  );
}

export default function MobileRoleAssign() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [roleResp, userResp] = await Promise.all([authAPI.roles(), authAPI.users()]);
      const roleList = Array.isArray(roleResp?.data) ? roleResp.data : [];
      const userList = Array.isArray(userResp?.data) ? userResp.data : [];
      setRoles(roleList);
      setUsers(userList);

      const nextEditing = {};
      userList.forEach((u) => {
        nextEditing[u.id] = u.role_name || 'LOGIN_USER';
      });
      setEditing(nextEditing);
    } catch (e) {
      message.error('获取用户/角色失败');
      setRoles([]);
      setUsers([]);
      setEditing({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleRoleChange = (userId, newRole) => {
    setEditing((prev) => ({
      ...prev,
      [userId]: newRole,
    }));
  };

  const handleSave = (record) => {
    const uid = record?.id;
    if (!uid) return;

    const nextRole = editing?.[uid] || 'LOGIN_USER';

    Modal.confirm({
      title: '确认修改角色',
      content: (
        <div>
          <div>{`确定要将用户"${record.username || uid}"的角色修改为 "${nextRole}" 吗？`}</div>
          <div style={{ color: 'red', marginTop: 8 }}>此操作会覆盖该用户原有角色（系统约定：一人仅一个角色）。</div>
        </div>
      ),
      okText: '确认修改',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setSavingUserId(uid);
        try {
          const resp = await authAPI.setUserRole(uid, nextRole);
          if (resp?.data?.ok) {
            message.success('修改成功');
          } else {
            message.error('修改失败');
          }
          await fetchAll();
        } catch (e) {
          message.error(e?.response?.data?.detail || '修改失败');
        } finally {
          setSavingUserId(null);
        }
      },
    });
  };

  const formatCreatedAt = (date) => {
    return date ? moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  const roleOptions = roles.map((r) => (
    <Option key={r.name} value={r.name}>
      {r.name}
    </Option>
  ));

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '16px 16px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <Title level={4} style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
            权限管理
          </Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              onClick={fetchAll}
              disabled={loading}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              刷新
            </Button>
            <Button
              size="small"
              onClick={() => navigate('/permissions', { replace: true })}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              返回
            </Button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin />
          </div>
        ) : (
          <div>
            {(users || []).map((row, idx) => (
              <Card key={row?.id ?? idx} style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, color: '#1677ff' }}>{row?.username || `用户 ${idx + 1}`}</div>
                  <Button size="small" type="primary" loading={savingUserId === row.id} icon={<SaveOutlined />} onClick={() => handleSave(row)}>
                    保存
                  </Button>
                </div>
                <Field label="用户ID" value={row?.id} />
                <Field label="邮箱" value={row?.email} />
                <Field label="启用" value={row?.enabled ? '是' : '否'} />
                <Field label="创建时间" value={formatCreatedAt(row?.created_at)} />
                <div style={{ marginTop: 8 }}>
                  <div style={{ width: 80, color: '#666', flex: '0 0 auto', marginBottom: 4 }}>角色</div>
                  <Select style={{ width: '100%' }} value={editing?.[row?.id] || 'LOGIN_USER'} onChange={(value) => handleRoleChange(row?.id, value)} placeholder="选择角色">
                    {roleOptions}
                  </Select>
                </div>
              </Card>
            ))}

            {!users || users.length === 0 ? (
              <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 12 } }}>
                <Text type="secondary">暂无用户数据</Text>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
